import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { createPortal } from 'react-dom';
import toast from 'react-hot-toast';
import { motion } from 'framer-motion';
import { useAuth } from '@context/AuthContext';
import { usePublicConfig } from '@context/PublicConfigContext';
import { resolveProfileDisplaySrc } from '@utils/profileImageSrc';
import Spinner from '@components/ui/Spinner';
import api from '@utils/api';
import { unauthenticatedEntryPath } from '@utils/authEntryPaths';
import ProfilePageHeader from '@components/profile/ProfilePageHeader';
import ProfileHeader from '@components/profile/ProfileHeader';
import ProfileActionButtons from '@components/profile/ProfileActionButtons';
import ProfileStats from '@components/profile/ProfileStats';
import InterestScroller from '@components/profile/InterestScroller';
import ProfileTabs from '@components/profile/ProfileTabs';
import SettingsDrawer from '@components/profile/SettingsDrawer';
import ProfileVibesSheet from '@components/profile/ProfileVibesSheet';
import SharedMemoriesSection from '@components/memories/SharedMemoriesSection';

const ProfilePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user: authUser, logout, updateUser } = useAuth();
  const { siteOrigin } = usePublicConfig();

  const [profile, setProfile] = useState(null);
  const [activities, setActivities] = useState([]);
  const [ratingSummary, setRatingSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [isSavingVisibility, setIsSavingVisibility] = useState(false);
  const [profilePhotoBusy, setProfilePhotoBusy] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [isEditingDetails, setIsEditingDetails] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [bioDraft, setBioDraft] = useState('');
  const [interestsDraft, setInterestsDraft] = useState('');
  const [isSavingProfileFields, setIsSavingProfileFields] = useState(false);
  const [profileTab, setProfileTab] = useState('grid');
  const [memoryTab, setMemoryTab] = useState('all');
  const [vibesOpen, setVibesOpen] = useState(false);

  const isOwnProfile = !id || id === authUser?._id;
  const userId = isOwnProfile ? authUser?._id : id;

  useEffect(() => {
    const fetchProfile = async () => {
      if (!userId) return;
      try {
        const { data } = await api.get(`/users/${userId}`);
        setAccessDenied(false);
        setProfile(data.user);
        setActivities(data.activities || []);
        const summaryRes = await api.get(`/users/${userId}/ratings`);
        setRatingSummary(summaryRes.data);
      } catch (error) {
        if (error?.response?.status === 403 && error?.response?.data?.isPrivateProfile) {
          setAccessDenied(true);
          setProfile(error.response.data.user || null);
          setActivities([]);
          setRatingSummary(null);
          return;
        }
        setProfile(isOwnProfile ? authUser : null);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [userId, isOwnProfile, authUser]);

  useEffect(() => {
    if (loading) return;
    const scrollToRatings = () => {
      document.getElementById('profile-ratings-summary')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    };
    if (location.hash === '#ratings') {
      const t = setTimeout(scrollToRatings, 320);
      return () => clearTimeout(t);
    }
    const onInboxOpen = () => scrollToRatings();
    window.addEventListener('playfynd:scroll-profile-ratings', onInboxOpen);
    return () => window.removeEventListener('playfynd:scroll-profile-ratings', onInboxOpen);
  }, [loading, location.hash, location.key, ratingSummary]);

  const displayUser = profile || authUser;

  const profileAvatarSrc = useMemo(
    () => resolveProfileDisplaySrc(displayUser, siteOrigin) || '',
    [displayUser, siteOrigin],
  );

  const vibesList = useMemo(() => {
    const profileUserId = String(displayUser?._id || userId || '');
    return [...activities]
      .map((a) => ({
        ...a,
        _isHosted:
          String(a?.creator?._id || a?.creator || '') === profileUserId,
      }))
      .sort((a, b) => {
        const ta = a.dateTime ? new Date(a.dateTime).getTime() : 0;
        const tb = b.dateTime ? new Date(b.dateTime).getTime() : 0;
        return tb - ta;
      });
  }, [activities, displayUser?._id, userId]);

  useEffect(() => {
    setNameDraft(displayUser?.name || '');
    setBioDraft(displayUser?.bio || '');
    setInterestsDraft((displayUser?.interests || []).join(', '));
  }, [displayUser?.name, displayUser?.bio, displayUser?.interests]);

  const profileVisibility = displayUser?.profileVisibility || 'public';

  const setProfileVisibility = async (nextVisibility) => {
    if (!isOwnProfile || nextVisibility === profileVisibility) return;
    try {
      setIsSavingVisibility(true);
      const { data } = await api.put('/auth/me', { profileVisibility: nextVisibility });
      setProfile(data.user);
      updateUser(data.user);
    } finally {
      setIsSavingVisibility(false);
    }
  };

  const handleProfilePhotoFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !isOwnProfile) return;
    setProfilePhotoBusy(true);
    try {
      const formData = new FormData();
      formData.append('profileImage', file);
      const { data } = await api.post('/upload-profile', formData);
      const base = profile || authUser || {};
      const next = { ...base, profileImage: data.imageUrl, profileImageUrl: '' };
      setProfile((prev) => (prev ? { ...prev, profileImage: data.imageUrl, profileImageUrl: '' } : next));
      updateUser(next);
      toast.success('Profile photo updated');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Upload failed');
    } finally {
      setProfilePhotoBusy(false);
    }
  };

  const saveProfileFields = async (payload, successMessage) => {
    try {
      setIsSavingProfileFields(true);
      const { data } = await api.put('/auth/me', payload);
      setProfile(data.user);
      updateUser(data.user);
      toast.success(successMessage);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not update profile');
    } finally {
      setIsSavingProfileFields(false);
    }
  };

  const handleSaveName = async () => {
    const trimmed = nameDraft.trim();
    if (!trimmed) {
      toast.error('Name is required');
      return;
    }
    await saveProfileFields({ name: trimmed }, 'Name updated');
    setIsEditingName(false);
  };

  const handleSaveDetails = async () => {
    const interests = interestsDraft
      .split(',')
      .map((interest) => interest.trim())
      .filter(Boolean);
    await saveProfileFields(
      { bio: bioDraft.trim().slice(0, 300), interests },
      'Profile details updated',
    );
    setIsEditingDetails(false);
  };

  const handleShare = async () => {
    const url = `${window.location.origin}/profile/${userId}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: displayUser?.name || 'Yaarvi profile', url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success('Profile link copied');
      }
    } catch {
      /* cancelled */
    }
  };

  const handleProfileTabChange = (next) => {
    setProfileTab(next);
    if (next === 'albums') setMemoryTab('albums');
    else if (next === 'shared') setMemoryTab('shared');
    else if (memoryTab === 'albums' || memoryTab === 'shared') setMemoryTab('all');
  };

  if (loading) return <Spinner className="min-h-screen" size="lg" />;

  if (!displayUser) {
    navigate(unauthenticatedEntryPath(), { replace: true });
    return null;
  }

  return (
    <div className="min-h-screen w-full bg-[#F7F8FC] dark:bg-[#070B16]">
      <ProfilePageHeader
        showSettings={isOwnProfile}
        onOpenSettings={() => setSettingsOpen(true)}
      />

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="mx-auto w-full max-w-3xl px-4 pb-28 pt-5 sm:px-6 lg:px-8 lg:pb-10"
      >
        <ProfileHeader
          user={displayUser}
          avatarSrc={profileAvatarSrc}
          vibesCount={activities.length}
          reviewsCount={ratingSummary?.totalReviews ?? displayUser?.totalReviews ?? 0}
          streak={displayUser?.activityStreak || 0}
          isOwnProfile={isOwnProfile}
          onUploadPhoto={handleProfilePhotoFile}
          photoBusy={profilePhotoBusy}
          onVibesClick={() => {
            if (accessDenied) return;
            setVibesOpen(true);
          }}
          onReviewsClick={() => {
            if (accessDenied) return;
            navigate(`/reviews/${userId}`);
          }}
        />

        <ProfileActionButtons
          isOwnProfile={isOwnProfile}
          onEdit={() => {
            if (isOwnProfile) setSettingsOpen(true);
          }}
          onShare={handleShare}
        />

        <ProfileStats
          playerRating={ratingSummary?.averagePlayerRating ?? displayUser?.averagePlayerRating}
          hostRating={ratingSummary?.averageHostRating ?? displayUser?.averageHostRating}
          safetyRating={ratingSummary?.averageSafetyRating ?? displayUser?.averageSafetyRating}
          streak={displayUser?.activityStreak || 0}
        />

        {!accessDenied && (
          <InterestScroller
            interests={displayUser?.interests || []}
            isOwnProfile={isOwnProfile}
            onAddInterest={() => {
              setIsEditingDetails(true);
              setSettingsOpen(false);
            }}
          />
        )}

        {accessDenied ? (
          <div className="mt-8 rounded-3xl border border-[#E9EEF7] bg-white px-6 py-12 text-center dark:border-white/[0.06] dark:bg-[#121826]">
            <p className="text-base font-semibold text-surface-800 dark:text-white">
              This profile is private.
            </p>
            <p className="mt-2 text-sm text-surface-500 dark:text-[#B5BDD1]">
              Joined vibes and memories are only visible to this user.
            </p>
          </div>
        ) : (
          <>
            <ProfileTabs active={profileTab} onChange={handleProfileTabChange} />
            <SharedMemoriesSection
              userId={userId}
              isOwnProfile={isOwnProfile}
              accessDenied={false}
              embedded
              tab={memoryTab}
              onTabChange={setMemoryTab}
            />
          </>
        )}
      </motion.div>

      {/* Edit sheets — portal, no AnimatePresence exit (avoids stuck overlays) */}
      {(isEditingName || isEditingDetails) &&
        typeof document !== 'undefined' &&
        createPortal(
          <div className="fixed inset-0 z-[1100]" role="presentation">
            <button
              type="button"
              className="absolute inset-0 bg-black/40 backdrop-blur-sm"
              aria-label="Close"
              onClick={() => {
                setIsEditingName(false);
                setIsEditingDetails(false);
              }}
            />
            <div className="absolute inset-x-0 bottom-0 z-10 max-h-[85vh] overflow-y-auto rounded-t-3xl bg-white p-5 pb-[calc(1.25rem+env(safe-area-inset-bottom,0px))] shadow-2xl dark:bg-[#121826] sm:inset-x-auto sm:bottom-auto sm:left-1/2 sm:top-1/2 sm:w-full sm:max-w-md sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-3xl sm:pb-5">
              {isEditingName && (
                <div className="space-y-4">
                  <h3 className="text-lg font-bold text-surface-900 dark:text-white">Edit name</h3>
                  <input
                    type="text"
                    value={nameDraft}
                    maxLength={50}
                    onChange={(e) => setNameDraft(e.target.value)}
                    className="input-field"
                  />
                  <div className="flex gap-2 pt-1">
                    <button
                      type="button"
                      disabled={isSavingProfileFields}
                      onClick={handleSaveName}
                      className="btn-primary flex-1 !py-3 !text-sm"
                    >
                      {isSavingProfileFields ? 'Saving…' : 'Save'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsEditingName(false)}
                      className="btn-secondary flex-1 !py-3 !text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
              {isEditingDetails && (
                <div className="space-y-4">
                  <h3 className="text-lg font-bold text-surface-900 dark:text-white">Edit details</h3>
                  <label className="block text-xs font-semibold text-surface-500 dark:text-surface-400">
                    Bio
                    <textarea
                      value={bioDraft}
                      maxLength={300}
                      rows={3}
                      onChange={(e) => setBioDraft(e.target.value)}
                      className="input-field mt-1"
                    />
                  </label>
                  <label className="block text-xs font-semibold text-surface-500 dark:text-surface-400">
                    Interests (comma-separated)
                    <input
                      type="text"
                      value={interestsDraft}
                      onChange={(e) => setInterestsDraft(e.target.value)}
                      className="input-field mt-1"
                    />
                  </label>
                  <div className="flex gap-2 pt-1">
                    <button
                      type="button"
                      disabled={isSavingProfileFields}
                      onClick={handleSaveDetails}
                      className="btn-primary flex-1 !py-3 !text-sm"
                    >
                      {isSavingProfileFields ? 'Saving…' : 'Save'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsEditingDetails(false)}
                      className="btn-secondary flex-1 !py-3 !text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>,
          document.body,
        )}

      {isOwnProfile && (
        <SettingsDrawer
          open={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          profileVisibility={profileVisibility}
          onVisibilityChange={setProfileVisibility}
          visibilitySaving={isSavingVisibility}
          onEditName={() => setIsEditingName(true)}
          onEditDetails={() => setIsEditingDetails(true)}
          onLogout={logout}
        />
      )}

      <ProfileVibesSheet
        open={vibesOpen}
        onClose={() => setVibesOpen(false)}
        activities={vibesList}
        title={isOwnProfile ? 'Your vibes' : `${displayUser?.name?.split(' ')[0] || 'User'}'s vibes`}
        onSelect={(a) => {
          setVibesOpen(false);
          navigate(`/activity/${a._id}`);
        }}
      />
    </div>
  );
};

export default ProfilePage;
